import type { DomParser } from "~/plugins/dom-parser"
import type { Relation } from "~/types/article"

/**
 * Creates a function that can be used to parse HTML strings with links to prepare them for displaying sidenotes.
 *
 * When calling the createHtmlFormatter function, a function is returned which can
 * be called to parse HTML strings in order to format it correctly to display sidenotes. This formatting function
 * which is returned loops over all "a" tags in the HTML string, and mutates the "a" tags inside it: Classes and
 * attributes are added based on which kind of identifier the matching relation has, and the href is updated to
 * contain a hash "#" as a prefix.
 *
 * @param formattedRelations - A list of relations to the given content being parsed, for instance an
 * article. Elements in the list contains attributes like `resourceUrl`, `identifier` and the rest of the content
 * used in sidenotes.
 * @param parser - A function that converts a string to a Document. Likely provided by the dom-parser plugin.
 *
 * @returns A function which takes the html string to format, and returns the parsed HTML.
 *
 * @example
 * // Example with a string that contains an "a" tag with a href, which is parsed to contain a hash and classes.
 * // Create the formatter and call the function with the HTML string to parse:
 *
 * const formatHtml = createHtmlFormatter(formattedRelations);
 * formatHtml("<a href='/kunne/sidenotater/exphil-uio/begrepsforklaring/indre-og-ytre-virkelighet'>[1]</a>");
 *
 * // Output:
 * "<a href='#/kunne/sidenotater/exphil-uio/begrepsforklaring/indre-og-ytre-virkelighet' class='text-link text-link--sidenote text-link--source' role='button'><sup>[1]</sup></a>"
 */
export function createHtmlFormatter(
  formattedRelations: Relation[],
  parser: DomParser,
): (html: string) => string {
  const sourceUrls: string[] = []

  return (html) => {
    const parsed = parser(html, ["mathml"])

    parsed.querySelectorAll("a").forEach((el) => {
      const [isRef = false, appendText] =
        // FIXME: Fix this regex issue, not 100% sure how without breaking it
        // eslint-disable-next-line regexp/no-super-linear-backtracking
        el.textContent?.trim().match(/^\[(?:\*|\d+)(.+)?\]$/) ?? []

      const idFromHref = el.getAttribute("data-location-id")

      const relation = formattedRelations.find(
        ({ locationId }) => locationId.toString() === idFromHref,
      )

      if (relation) {
        el.href = `#${relation.locationId}`
        el.classList.add("text-link", "text-link--sidenote")
        el.setAttribute("role", "button")

        if (isRef) {
          relation.appendText = appendText
          relation.identifier = "source"
        }

        switch (relation.identifier) {
          case "source": {
            let index =
              sourceUrls.indexOf(relation.locationId.toString()) + 1
            if (!index) {
              sourceUrls.push(relation.locationId.toString())
              index = sourceUrls.length
            }

            relation.title = `${index}.`
            el.innerHTML = `<sup>[${index}]</sup>`
            el.classList.add("text-link--source")

            if (relation.appendText && relation.html) {
              const { html, appendText } = relation
              const index = html?.lastIndexOf(".")
              if (index >= 0) {
                relation.html = `${html.substring(
                  0,
                  index,
                )}, ${appendText}.${html.substring(index + 1)}`
              }
            }
            break
          }

          case "what_is": {
            el.classList.add("text-link--what_is")

            relation.resource.explanation = parser(relation.resource.explanation, ["mathml"]).body.innerHTML

            break
          }
          case "explainer":
          case "sidenote": {
            relation.identifier = "explainer"
            el.classList.add("text-link--explainer")
            break
          }
        }
      }
    })

    return parsed.body.innerHTML
  }
}
